import { useState, type ReactElement } from 'react';
import { createGenerateId, JssProvider, SheetsRegistry } from 'react-jss';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactINTLProvider } from '@picsart/rc/services/localization';
import type { ILanguagesCodes } from '@picsart/rc/types/data/languages';

import { useThemeChange } from '@/hooks/useThemeChange';
import { useMount } from '@/hooks/useMount';
import { getContext } from '@/context';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const registry = new SheetsRegistry();
const generateId = createGenerateId({
  minify: import.meta.env.MODE === 'production',
});

// Get initial language safely
const getInitialLanguage = (): ILanguagesCodes => {
  try {
    return getContext().general.language.getState() as ILanguagesCodes;
  } catch {
    return 'en' as ILanguagesCodes;
  }
};

const JSSProviderWrapper = ({ children }: { children: ReactElement }) => {
  useThemeChange();
  return (
    <JssProvider generateId={generateId} registry={registry}>
      {children}
    </JssProvider>
  );
};

interface WrappersProps {
  children: ReactElement;
  setupTheming?: boolean;
}

const Wrappers = ({ children, setupTheming = true }: WrappersProps) => {
  const [language, setLanguage] = useState<ILanguagesCodes>(
    getInitialLanguage()
  );

  useMount(() => {
    try {
      getContext().general.language.subscribe((nextLanguage: ILanguagesCodes) =>
        setLanguage(nextLanguage)
      );
    } catch {
      // Language subscription failed
    }
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ReactINTLProvider
        projectName="pa_miniapps"
        language={language}
        withoutReusableComponents
      >
        {setupTheming ? (
          <JSSProviderWrapper>{children}</JSSProviderWrapper>
        ) : (
          <JssProvider generateId={generateId} registry={registry}>
            {children}
          </JssProvider>
        )}
      </ReactINTLProvider>
    </QueryClientProvider>
  );
};

export default Wrappers;
